import React, { useEffect, useMemo, useRef } from 'react';

import { useInvoiceEditContext } from 'lib/context/InvoiceContext/InvoiceContext';
import { cn, iaRa } from 'lib/helpers/utility';
import { useOneInternalInvoice } from 'lib/hooks/queries/billing/useOneInternalInvoice';

import AGBasicTable from 'components/shared/AGBasicTable/AGBasicTable';
import CustomLoadingOverlay from 'components/shared/AGTable/customLoadingOverlay';
import CustomNoRowsOverlay from 'components/shared/AGTable/customNoRowsOverlay';

import { EDIT_INVOICE_COLUMN_DEFS, EDIT_INVOICE_DEFAULT_COLUMN_DEFS } from './lib/columnDefs';
import { updateInvoiceData } from './lib/helpers';
import { addTempIdToInvoice, addTempIdToItem } from './lib/tempIdHandler';

const EditInvoiceModal = ({ invoiceId }) => {
  const { editedInvoice, setEditedInvoice } = useInvoiceEditContext();
  const gridRef = useRef(null);
  const containerRef = useRef(null);
  const previousInvoiceIdRef = useRef(null);

  const { data, isLoading } = useOneInternalInvoice({
    params: { id: invoiceId },
    dependencies: [invoiceId],
    options: {
      enabled: Boolean(invoiceId)
    }
  });

  const editedInvoiceRef = useRef(editedInvoice);

  useEffect(() => {
    const invoiceIdChanged = previousInvoiceIdRef.current !== invoiceId;
    if (data?.invoice && (!editedInvoice || invoiceIdChanged)) {
      setEditedInvoice(addTempIdToInvoice(data?.invoice));
      previousInvoiceIdRef.current = invoiceId;
      editedInvoiceRef.current = addTempIdToInvoice(data?.invoice);
    }
  }, [data?.invoice, invoiceId, editedInvoice, setEditedInvoice]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const gridApi = gridRef.current?.api;
      if (!gridApi || !containerRef.current) return;

      if (containerRef.current.contains(event.target)) return;

      const editingCells = gridApi.getEditingCells();
      if (editingCells.length > 0) {
        gridApi.stopEditing();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const rowData = useMemo(() => {
    const invoice = editedInvoice || data?.invoice;
    if (!invoice) return;

    const procedures = iaRa(invoice.procedures).map((procedure) => ({
      ...addTempIdToItem(procedure),
      type: procedure?.type || 'Package'
    }));
    const products = iaRa(invoice.products).map((product) => ({
      ...addTempIdToItem(product),
      type: 'Product'
    }));
    const packages = iaRa(invoice.packages).map((pkg) => ({
      ...addTempIdToItem(pkg),
      type: 'Package'
    }));
    const finalItems = [...procedures, ...products, ...packages];
    return finalItems;
  }, [editedInvoice, data?.invoice]);

  const calcHeight = useMemo(() => {
    return iaRa(rowData).length * 40 + 52 + 45; // 40px is the height of the row, 52px is the height of the footer, 45px is the height of the header
  }, [rowData]);

  const onCellEditingStopped = async (params) => {
    const { column, data: editedRowData } = params;

    const colDef = column.colDef;

    const newInvoice = await updateInvoiceData(colDef, editedRowData, editedInvoiceRef.current);
    setEditedInvoice(newInvoice);
    editedInvoiceRef.current = newInvoice;
  };

  const components = useMemo(
    () => ({
      agLoadingOverlay: CustomLoadingOverlay,
      agNoRowsOverlay: CustomNoRowsOverlay
    }),
    []
  );

  return (
    <div
      ref={containerRef}
      className={cn('max-h-[calc(50vh-80px)] min-h-[160px] w-full sm:max-h-[calc(50vh-32px)]')}
      style={{ height: `${calcHeight}px` }}>
      <AGBasicTable
        key={invoiceId}
        columnDefs={EDIT_INVOICE_COLUMN_DEFS}
        defaultColDef={EDIT_INVOICE_DEFAULT_COLUMN_DEFS}
        rowData={rowData}
        gridRef={gridRef}
        loading={isLoading}
        components={components}
        singleClickEdit={true}
        getRowId={(params) => params.data.tempId}
        onCellEditingStopped={onCellEditingStopped}
        context={{
          triggerCellStopped: (params) => onCellEditingStopped(params)
        }}
      />
    </div>
  );
};

export default EditInvoiceModal;
